<script setup lang="ts">
import type { PropType } from "vue";
import type { Swiper as SwiperInstance } from "swiper";

defineProps({
  groups: {
    type: Array as PropType<string[]>,
    required: true,
  },
});

const selectedGroup = defineModel<string>("selectedGroup");

function changeSelectedGroup(group: string) {
  selectedGroup.value = group;
}

const swiperInstance = ref<SwiperInstance | null>(null);
const isBeginning = ref(true);
const isEnd = ref(false);

function onSwiperInit(swiper: SwiperInstance) {
  swiperInstance.value = swiper;
  isBeginning.value = swiper.isBeginning;
  isEnd.value = swiper.isEnd;

  swiper.on("slideChange", () => {
    isBeginning.value = swiper.isBeginning;
    isEnd.value = swiper.isEnd;
  });
}

function nextSlide() {
  if (swiperInstance.value) {
    swiperInstance.value.slideNext();
  }
}

function prevSlide() {
  if (swiperInstance.value) {
    swiperInstance.value.slidePrev();
  }
}
</script>

<template>
  <ClientOnly>
    <div class="flex justify-center">
      <div
        class="mb-12 flex cursor-pointer overflow-hidden rounded-lg border border-surface-border"
      >
        <div
          :class="[
            'flex items-center justify-center px-8',
            { 'cursor-not-allowed opacity-20': isBeginning },
          ]"
          @click="prevSlide"
        >
          <font-awesome-icon :icon="['fas', 'chevron-left']" />
        </div>

        <div class="max-w-[256px] md:max-w-[512px]">
          <Swiper
            :breakpoints="{
              300: {
                slidesPerView: groups.length >= 2 ? 2 : groups.length,
              },
              1024: {
                slidesPerView: groups.length >= 4 ? 4 : groups.length,
              },
            }"
            @swiper="onSwiperInit"
          >
            <SwiperSlide v-for="(group, index) in groups" :key="index">
              <div
                :class="[
                  'relative w-32 px-5 py-4 text-center',
                  { 'bg-[#9E23D280]': selectedGroup === group },
                ]"
                @click="changeSelectedGroup(group)"
              >
                <span
                  v-if="index !== 0"
                  class="absolute left-0 top-1/2 h-[60%] w-px -translate-y-1/2 transform bg-surface-border"
                />

                <div class="font-extrabold">
                  {{ group.split(" ")[0] }}
                </div>
                <div class="text-xs font-medium">{{ group.split(" ")[1] }}</div>
              </div>
            </SwiperSlide>
          </Swiper>
        </div>

        <div
          :class="[
            'flex items-center justify-center px-8',
            { 'cursor-not-allowed opacity-20': isEnd },
          ]"
          @click="nextSlide"
        >
          <font-awesome-icon :icon="['fas', 'chevron-right']" />
        </div>
      </div>
    </div>
  </ClientOnly>
</template>

<style scoped></style>
